// =============================================================================
// features/crm/listFilters.ts — Regras puras dos filtros e dos cartões da lista
// do CRM (F58-S08: CRM-03 filtros, CRM-05 cartões).
//
// Sem React: tudo aqui é testável sem DOM. O estado vive na URL
// (`/crm?stale=true` precisa funcionar a partir do dashboard); a tela só lê e
// escreve `LeadUrlFilters` e desenha o que estas funções devolvem.
// =============================================================================

import type { LeadStatsResponse } from '@elemento/shared-schemas';

import type { LeadUrlFilters } from '../../hooks/crm/leadFilters';
import { STATUS_META } from '../../hooks/crm/types';

/** Qual filtro uma etiqueta removível desliga. */
export type FilterChipKey =
  | 'search'
  | 'status'
  | 'no_agent'
  | 'stale'
  | 'created_from'
  | 'created_to';

export interface ActiveFilterChip {
  key: FilterChipKey;
  label: string;
}

/** `2026-11-01` → `01/11/2026`, sem passar por `Date` (sem deslocar o dia por fuso). */
export function formatDayLabel(day: string): string {
  const [y, m, d] = day.split('-');
  return y && m && d ? `${d}/${m}/${y}` : day;
}

/** Etiquetas dos filtros ativos, na ordem em que aparecem na barra. */
export function activeFilterChips(filters: LeadUrlFilters): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];
  if (filters.search) chips.push({ key: 'search', label: `Busca: ${filters.search}` });
  if (filters.status) {
    chips.push({ key: 'status', label: `Status: ${STATUS_META[filters.status].label}` });
  }
  if (filters.no_agent) chips.push({ key: 'no_agent', label: 'Sem agente' });
  if (filters.stale) chips.push({ key: 'stale', label: 'Parados há mais de 7 dias' });
  if (filters.created_from) {
    chips.push({
      key: 'created_from',
      label: `Criados a partir de ${formatDayLabel(filters.created_from)}`,
    });
  }
  if (filters.created_to) {
    chips.push({ key: 'created_to', label: `Criados até ${formatDayLabel(filters.created_to)}` });
  }
  return chips;
}

/** Desliga um filtro e volta à primeira página (o conjunto mudou). */
export function removeFilter(filters: LeadUrlFilters, key: FilterChipKey): LeadUrlFilters {
  const { [key]: _removed, ...rest } = filters;
  return { ...rest, page: 1 };
}

/** Sem nenhum filtro (nem página): lista "limpa". */
export function clearedFilters(): LeadUrlFilters {
  return { page: 1 };
}

/**
 * Período de criação coerente: o servidor recusa `created_from` depois de
 * `created_to` (400). Ao mexer em uma ponta que passaria da outra, a outra
 * acompanha — o usuário nunca monta um intervalo invertido.
 */
export function withCreatedRange(
  filters: LeadUrlFilters,
  edge: 'created_from' | 'created_to',
  value: string,
): LeadUrlFilters {
  const next: LeadUrlFilters = { ...filters, page: 1 };
  if (!value) {
    delete next[edge];
    return next;
  }
  next[edge] = value;
  if (edge === 'created_from' && next.created_to && value > next.created_to) {
    next.created_to = value;
  }
  if (edge === 'created_to' && next.created_from && value < next.created_from) {
    next.created_from = value;
  }
  return next;
}

// ─── Cartões do topo ──────────────────────────────────────────────────────────

export interface StatCardModel {
  key: 'total' | 'new_in_month' | 'qualifying' | 'closed_won';
  label: string;
  value: number;
  description: string;
}

/**
 * Quatro cartões a partir de `GET /api/leads/stats` — o mesmo conjunto da
 * lista, não a página de 20. "Convertidos" é contagem real (`closed_won`) e a
 * participação no total vai na descrição; não há seta de tendência inventada.
 */
export function buildStatCards(stats: LeadStatsResponse, filtered: boolean): StatCardModel[] {
  const share = stats.total > 0 ? Math.round((stats.by_status.closed_won / stats.total) * 100) : 0;
  return [
    {
      key: 'total',
      label: 'Total de leads',
      value: stats.total,
      description: filtered ? 'Com os filtros aplicados' : 'Todos os leads que você pode ver',
    },
    {
      key: 'new_in_month',
      label: 'Novos no mês',
      value: stats.new_in_month,
      description: 'Criados neste mês',
    },
    {
      key: 'qualifying',
      label: 'Em qualificação',
      value: stats.by_status.qualifying,
      description: 'Status: qualificando',
    },
    {
      key: 'closed_won',
      label: 'Convertidos',
      value: stats.by_status.closed_won,
      description: `${share}% do total`,
    },
  ];
}
